import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { userSession, categoryApi, productApi, pointsApi } from '../services';
import { formatMoney } from '../utils/utils';

function OrderPage({ onNavigate }) {
  const [userInfo, setUserInfo] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCartDetail, setShowCartDetail] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  useEffect(() => {
    // Get user session data
    const session = userSession.getSession();
    if (session) {
      setUserInfo(session);
      
      // Load cart from session if exists
      if (session.cart && Array.isArray(session.cart)) {
        console.log('Loading cart from session:', session.cart);
        setCart(session.cart);
      }
      
      loadCategories();
      // Check subscription if user is logged in
      checkCustomerSubscription();
    } else {
      // Redirect to checkin if no session
      onNavigate('/checkin');
    }
  }, [onNavigate]);

  // Save cart to session whenever cart changes
  useEffect(() => {
    if (userInfo) {
      console.log('Saving cart to session:', cart);
      userSession.updateSession({ cart });
    }
  }, [cart, userInfo]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await categoryApi.getCategories({
        search: '',
        page_size: 10,
        page_number: 1,
        status: 0
      });
      
      console.log(response);
      if (response.payload && response.payload.length > 0) {
        setCategories(response.payload);
        setSelectedCategory(response.payload[0].category_id);
        // Load products for first category
        await loadProducts(response.payload[0].category_id);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      setError('Không thể tải danh mục sản phẩm');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async (categoryId) => {
    try {
      setLoading(true);
      const response = await productApi.getProducts({
        search: '',
        page_size: 100,
        page_number: 1,
        status: 0,
        category_id: categoryId
      });
      
      if (response.payload) {
        setProducts(response.payload);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      setError('Không thể tải danh sách sản phẩm');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = async (categoryId) => {
    setSelectedCategory(categoryId);
    await loadProducts(categoryId);
  };

  // Get quantity of product in cart
  const getProductQuantityInCart = (productId) => {
    const item = cart.find(item => item.product_id === productId);
    return item ? item.quantity : 0;
  };

  // Thêm sản phẩm vào giỏ
  const handleAddToCart = (product) => {
    // Kiểm tra nếu sản phẩm là combo giờ (category_type = 2) và khách hàng có vé tháng còn hiệu lực
    if (product.category_type === 2 && hasActiveSubscription) {
      alert('Khách hàng đã có vé tháng còn hiệu lực, không thể chọn combo giờ');
      return;
    }

    // Kiểm tra nếu sản phẩm là combo giờ (category_type = 2) và đơn hàng đã có vé tháng
    if (product.category_type === 2) {
      const hasSubscription = cart.some(
        (item) => item.category_type === 3 && !item.is_free_subscription
      );

      if (hasSubscription) {
        alert('Đơn hàng đã có vé tháng, không thể thêm combo giờ');
        return;
      }
    }

    // Kiểm tra nếu sản phẩm là vé tháng (category_type = 3)
    if (product.category_type === 3) {
      // Kiểm tra nếu đã có combo giờ trong đơn hàng
      const hasComboHour = cart.some(
        (item) => item.category_type === 2
      );

      if (hasComboHour) {
        alert('Đơn hàng không thể vừa có combo giờ, vừa có vé tháng');
        return;
      }

      // Kiểm tra nếu khách hàng đã có vé tháng còn hiệu lực
      if (hasActiveSubscription) {
        alert('Khách hàng đã có vé tháng, không thể mua vé mới');
        return;
      }

      // Kiểm tra xem đã có vé tháng khác trong giỏ hàng chưa (trừ vé tháng miễn phí)
      const hasOtherSubscription = cart.some(
        (item) => item.category_type === 3 && !item.is_free_subscription
      );

      if (hasOtherSubscription) {
        alert('Trong 1 đơn hàng chỉ được chọn 1 loại vé tháng');
        return;
      }
    }

    setCart(prev => {
      const found = prev.find(item => item.product_id === product.product_id);
      if (found) {
        return prev.map(item => item.product_id === product.product_id ? { ...item, quantity: item.quantity + 1 } : item);
      } else {
        return [...prev, { ...product, quantity: 1 }];
      }
    });
  };

  // Tăng số lượng (dùng chung cho product list và cart)
  const handleIncrease = (productId) => {
    setCart(prev => {
      const found = prev.find(item => item.product_id === productId);
      if (found) {
        return prev.map(item => item.product_id === productId ? { ...item, quantity: item.quantity + 1 } : item);
      } else {
        // If not in cart, find product and add it
        const product = products.find(p => p.product_id === productId);
        if (product) {
          // Kiểm tra nếu sản phẩm là combo giờ (category_type = 2) và khách hàng có vé tháng còn hiệu lực
          if (product.category_type === 2 && hasActiveSubscription) {
            alert('Khách hàng đã có vé tháng còn hiệu lực, không thể chọn combo giờ');
            return prev;
          }

          // Kiểm tra nếu sản phẩm là combo giờ (category_type = 2) và đơn hàng đã có vé tháng
          if (product.category_type === 2) {
            const hasSubscription = prev.some(
              (item) => item.category_type === 3 && !item.is_free_subscription
            );

            if (hasSubscription) {
              alert('Đơn hàng đã có vé tháng, không thể thêm combo giờ');
              return prev;
            }
          }

          // Kiểm tra nếu sản phẩm là vé tháng (category_type = 3)
          if (product.category_type === 3) {
            // Kiểm tra nếu đã có combo giờ trong đơn hàng
            const hasComboHour = prev.some(
              (item) => item.category_type === 2
            );

            if (hasComboHour) {
              alert('Đơn hàng không thể vừa có combo giờ, vừa có vé tháng');
              return prev;
            }

            // Kiểm tra nếu khách hàng đã có vé tháng còn hiệu lực
            if (hasActiveSubscription) {
              alert('Khách hàng đã có vé tháng, không thể mua vé mới');
              return prev;
            }

            // Kiểm tra xem đã có vé tháng khác trong giỏ hàng chưa (trừ vé tháng miễn phí)
            const hasOtherSubscription = prev.some(
              (item) => item.category_type === 3 && !item.is_free_subscription
            );

            if (hasOtherSubscription) {
              alert('Trong 1 đơn hàng chỉ được chọn 1 loại vé tháng');
              return prev;
            }
          }
          return [...prev, { ...product, quantity: 1 }];
        }
        return prev;
      }
    });
  };

  // Giảm số lượng (dùng chung cho product list và cart)
  const handleDecrease = (productId) => {
    setCart(prev => prev
      .map(item => item.product_id === productId ? { ...item, quantity: item.quantity - 1 } : item)
      .filter(item => item.quantity > 0)
    );
  };

  // Xoá sản phẩm khỏi giỏ
  const handleRemove = (id) => {
    setCart(prev => prev.filter(item => item.product_id !== id));
  };

  // Clear cart (for manual clear or after successful checkin)
  const clearCart = () => {
    setCart([]);
    userSession.updateSession({ cart: [] });
  };

  // Tính tổng tiền giỏ hàng
  const getTotalAmount = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  // Tính tổng số lượng sản phẩm
  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  // Kiểm tra vé tháng của khách hàng
  const checkCustomerSubscription = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      // Nếu không có token, không thể kiểm tra subscription
      setHasActiveSubscription(false);
      return;
    }

    try {
      const response = await pointsApi.getCustomerInfo();
      if (response.ok && response.payload) {
        const customerData = response.payload;
        const hasSubscription = 
          customerData.subscription_product_id !== null &&
          customerData.subscription_product_id !== undefined;
        
        const subscriptionEndDate = customerData.subscription_end_date
          ? new Date(customerData.subscription_end_date)
          : null;
        const now = new Date();

        // Kiểm tra nếu có vé tháng và còn hiệu lực
        const isActive = hasSubscription && subscriptionEndDate && subscriptionEndDate >= now;
        
        setHasActiveSubscription(isActive);
        setSubscriptionInfo({
          subscription_product_id: customerData.subscription_product_id,
          subscription_start_date: customerData.subscription_start_date,
          subscription_end_date: customerData.subscription_end_date
        });
      } else {
        setHasActiveSubscription(false);
        setSubscriptionInfo(null);
      }
    } catch (error) {
      console.error('Error checking customer subscription:', error);
      setHasActiveSubscription(false);
      setSubscriptionInfo(null);
    }
  };

  // Filter products: nếu khách hàng có vé tháng thì ẩn category_type 2 và 3
  const filteredProducts = hasActiveSubscription
    ? products.filter(p => p.category_type !== 2 && p.category_type !== 3)
    : products;

  // Handle checkin - save order data and navigate
  const handleCheckin = () => {
    if (cart.length === 0) return;

    // Navigate to summary page instead of direct checkin
    onNavigate('/order-summary');
  };

  if (!userInfo) {
    return <div>Loading...</div>;
  }

  if (loading && categories.length === 0) {
    return (
      <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: 40 }}>
        <div style={{ background: '#fff', width: 340, borderRadius: 8, padding: 40, textAlign: 'center' }}>
          <div>Đang tải dữ liệu...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: 40 }}>
        <div style={{ background: '#fff', width: 340, borderRadius: 8, padding: 40, textAlign: 'center' }}>
          <div style={{ color: '#ff4444', marginBottom: 16 }}>{error}</div>
          <button 
            onClick={loadCategories}
            style={{
              background: '#5b6754',
              color: '#fff',
              border: 'none',
              borderRadius: 20,
              padding: '10px 20px',
              cursor: 'pointer'
            }}
          >
            Thử lại
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
      <div style={{ background: '#fff', width: 340, minHeight: 600, borderRadius: 8, padding: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
        <Header onCartClick={() => { onNavigate(-1); }} onMenuClick={() => { onNavigate('/') }} />
        
        {/* Tabs danh mục */}
        <div style={{ display: 'flex', width: '100%', marginBottom: 16, overflowX: 'auto', whiteSpace: 'nowrap', gap: 0 }}>
          {categories.map((cat, idx) => (
            <button
              key={cat.category_id}
              style={{
                display: 'inline-block',
                minWidth: 90,
                padding: '12px 0',
                background: selectedCategory === cat.category_id ? '#5b6754' : '#fff',
                color: selectedCategory === cat.category_id ? '#fff' : '#222',
                border: 'none',
                borderRadius: 20,
                marginRight: 12,
                fontWeight: 400,
                fontSize: 17,
                cursor: 'pointer',
                fontFamily: 'NeueEinstellung',
                boxShadow: selectedCategory === cat.category_id ? '0 2px 8px rgba(91,103,84,0.08)' : 'none',
                transition: 'background 0.2s',
              }}
              onClick={() => handleCategoryChange(cat.category_id)}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Loading indicator for products */}
        {loading && (
          <div style={{ width: '100%', textAlign: 'center', padding: '20px 0' }}>
            <div>Đang tải sản phẩm...</div>
          </div>
        )}

        {/* Danh sách sản phẩm */}
        {!loading && (
          <div style={{ width: '100%', marginBottom: 24, flex: 1, overflowY: 'auto' }}>
            {filteredProducts.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#888', padding: '20px 0' }}>
                {hasActiveSubscription 
                  ? 'Bạn đã có vé tháng, không thể mua thêm combo giờ hoặc vé tháng mới'
                  : 'Không có sản phẩm nào trong danh mục này'}
              </div>
            ) : (
              filteredProducts.map(p => {
                const quantityInCart = getProductQuantityInCart(p.product_id);
                return (
                  <div key={p.product_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #eee' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500 }}>{p.name}</div>
                      <div style={{ color: '#888', fontSize: 14 }}>{formatMoney(p.price)}đ</div>
                    </div>
                    
                    {/* Quantity controls in product list */}
                    {quantityInCart === 0 ? (
                      <button 
                        onClick={() => handleAddToCart(p)} 
                        style={{ 
                          background: '#5b6754', 
                          color: '#fff', 
                          border: 'none', 
                          borderRadius: 16, 
                          padding: '6px 16px', 
                          fontWeight: 500, 
                          cursor: 'pointer', 
                          fontFamily: 'NeueEinstellung' 
                        }}
                      >
                        Thêm
                      </button>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button 
                          onClick={() => handleDecrease(p.product_id)} 
                          style={productQuantityBtnStyle}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5b6754" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="5" y1="12" x2="19" y2="12" />
                          </svg>
                        </button>
                        <span style={{ 
                          minWidth: 24, 
                          textAlign: 'center', 
                          fontWeight: 600, 
                          fontSize: 16,
                          color: '#5b6754'
                        }}>
                          {quantityInCart}
                        </span>
                        <button 
                          onClick={() => handleIncrease(p.product_id)} 
                          style={productQuantityBtnStyle}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5b6754" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Fixed Cart and Checkin Section at Bottom */}
        <div style={{ 
          position: 'fixed', 
          bottom: 0, 
          left: '50%', 
          transform: 'translateX(-50%)', 
          width: 340, 
          background: '#fff', 
          borderTop: '1px solid #eee',
          padding: '16px 20px',
          borderRadius: '16px 16px 0 0',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
          zIndex: 1000
        }}>
          {/* Cart Summary */}
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              marginBottom: 12,
              cursor: 'pointer',
              padding: '8px 0'
            }}
            onClick={() => setShowCartDetail(!showCartDetail)}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ 
                background: '#5b6754', 
                color: '#fff', 
                borderRadius: '50%', 
                width: 24, 
                height: 24, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: 12, 
                fontWeight: 'bold',
                marginRight: 8
              }}>
                {getTotalItems()}
              </div>
              <span style={{ fontWeight: 500, fontSize: 16 }}>Giỏ hàng</span>
              {cart.length > 0 && (
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    clearCart();
                  }}
                  style={{
                    background: 'transparent',
                    color: '#888',
                    border: 'none',
                    fontSize: 12,
                    cursor: 'pointer',
                    marginLeft: 8,
                    textDecoration: 'underline'
                  }}
                >
                  Xóa tất cả
                </button>
              )}
            </div>
            <div style={{ fontWeight: 600, fontSize: 18, color: '#5b6754' }}>
              {formatMoney(getTotalAmount())}đ
            </div>
          </div>

          {/* Cart Detail (Expandable) */}
          {showCartDetail && cart.length > 0 && (
            <div style={{ 
              background: '#f8f8f8', 
              borderRadius: 12, 
              padding: 16, 
              marginBottom: 12,
              maxHeight: 200,
              overflowY: 'auto'
            }}>
              {cart.map(item => (
                <div key={item.product_id} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  marginBottom: 8,
                  paddingBottom: 8,
                  borderBottom: '1px solid #eee'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{item.name}</div>
                    <div style={{ color: '#888', fontSize: 12 }}>{formatMoney(item.price)}đ</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button onClick={() => handleDecrease(item.product_id)} style={smallCircleBtnStyle}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5b6754" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </button>
                    <span style={{ minWidth: 20, textAlign: 'center', fontWeight: 500, fontSize: 14 }}>{item.quantity}</span>
                    <button onClick={() => handleIncrease(item.product_id)} style={smallCircleBtnStyle}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5b6754" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </button>
                    <button onClick={() => handleRemove(item.product_id)} style={smallRemoveBtnStyle}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="red" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Checkin Button */}
          <button 
            style={{
              ...btnStyle,
              width: '100%',
              opacity: cart.length === 0 ? 0.5 : 1,
              cursor: cart.length === 0 ? 'not-allowed' : 'pointer'
            }} 
            onClick={handleCheckin}
            disabled={cart.length === 0}
          >
            Check-in
          </button>
        </div>

        {/* Bottom padding to prevent content from being hidden behind fixed cart */}
        <div style={{ height: 120 }}></div>
      </div>
    </div>
  );
}

const btnStyle = {
  height: 44,
  background: '#5b6754',
  color: '#fff',
  border: 'none',
  borderRadius: 24,
  fontSize: 18,
  fontWeight: 300,
  cursor: 'pointer',
  fontFamily: 'NeueEinstellung',
};

const productQuantityBtnStyle = {
  width: 28,
  height: 28,
  borderRadius: '50%',
  border: '1.5px solid #5b6754',
  background: '#fff',
  color: '#5b6754',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  outline: 'none',
  margin: 0,
  padding: 0,
  transition: 'background 0.2s, color 0.2s',
};

const smallCircleBtnStyle = {
  width: 24,
  height: 24,
  borderRadius: '50%',
  border: '1px solid #5b6754',
  background: '#fff',
  color: '#5b6754',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  outline: 'none',
  margin: 0,
  padding: 0,
  transition: 'background 0.2s, color 0.2s',
};

const smallRemoveBtnStyle = {
  width: 20,
  height: 20,
  borderRadius: '50%',
  border: '1px solid #e74c3c',
  background: '#fff',
  color: '#e74c3c',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  outline: 'none',
  margin: 0,
  padding: 0,
  transition: 'background 0.2s, color 0.2s',
};

export default OrderPage;
